/**
 * RoleGuard component - protects routes based on user roles
 *
 * Handles loading, unauthenticated, and unauthorized states.
 * Supports fallback component for unauthorized access.
 *
 * Validates: Requirements 1.5, 2.5, 3.2, 4.2
 */

import React from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { usePermissions } from './usePermissions';
import { UnauthorizedPage } from '../components/UnauthorizedPage';
import type { AppRole } from './types';

export interface RoleGuardProps {
  /** Roles required to access the protected content (user needs at least one) */
  requiredRoles: AppRole[];
  /** Content to render when user is authorized */
  children: React.ReactNode;
  /** Optional fallback component for unauthorized users */
  fallback?: React.ReactNode;
  /** Optional custom loading component */
  loadingComponent?: React.ReactNode;
}

/**
 * Guards content based on user roles
 *
 * - Shows loading spinner while auth state is being determined
 * - Redirects to login if user is not authenticated
 * - Shows unauthorized page (or custom fallback) if user lacks required roles
 * - Renders children if user has at least one of the required roles
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  requiredRoles,
  children,
  fallback,
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, hasAnyRole, login } = usePermissions();

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      loadingComponent ?? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Spinner size={SpinnerSize.large} label="Loading..." />
        </div>
      )
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Trigger login redirect
    login();
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Spinner size={SpinnerSize.large} label="Redirecting to login..." />
      </div>
    );
  }

  // Check if user has required roles
  if (!hasAnyRole(requiredRoles)) {
    return <>{fallback ?? <UnauthorizedPage />}</>;
  }

  // User is authenticated and authorized
  return <div data-testid="authorized">{children}</div>;
};

/**
 * Determines access based on user roles and required roles
 * Pure function for testing access control logic
 *
 * @param userRoles - Roles the user has
 * @param requiredRoles - Roles required for access
 * @returns true if user has at least one required role
 */
export function checkAccess(userRoles: AppRole[], requiredRoles: AppRole[]): boolean {
  if (requiredRoles.length === 0) return true;
  return requiredRoles.some((role) => userRoles.includes(role));
}
